/**
 * UserProfileDrawer.tsx
 *
 * Slide-over drawer displaying rich user profile, endorsements, and badges.
 */

import React, { useState } from 'react';
import { UserAvatar, getCategoryVectorIcon } from './UserAvatar.js';
import { ReactionBar } from './ReactionBar.js';
import {
  ShieldIcon,
  CheckCircleIcon,
  AwardIcon,
  FileTextIcon,
  SparklesIcon,
  ActivityIcon,
  UserIcon,
  LayersIcon,
} from '../icons.js';
import { useBreakpoint } from '../useBreakpoint.js';
import type { MeshUserProfileReadModel } from '../../identity/projections/user-profile-projection.js';
import type { ExpertiseScoreReadModel } from '../../identity/projections/reputation-projection.js';
import type { BadgeRecord } from '../../identity/domain/badge-records.js';
import type { ProfileVisibility } from '../../identity/domain/profile-claims.js';

export interface UserProfileDrawerProps {
  readonly profile: MeshUserProfileReadModel;
  readonly expertiseScores?: readonly ExpertiseScoreReadModel[];
  readonly activeBadges?: readonly BadgeRecord[];
  readonly pendingNominations?: readonly BadgeRecord[];
  readonly viewerId: string;
  readonly viewerTier: ProfileVisibility;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onEndorseSkill?: (categoryId: string) => void;
  readonly onReact?: (reaction: 'helpful' | 'star' | 'insight') => void;
  readonly onOpenPhotoEditor?: () => void;
  readonly onApproveBadge?: (badgeInstanceKey: string) => void;
  readonly onRevokeBadge?: (badgeInstanceKey: string, reason: string) => void;
}

export const UserProfileDrawer: React.FC<UserProfileDrawerProps> = ({
  profile,
  expertiseScores = [],
  activeBadges = [],
  isOpen,
  onClose,
  onOpenPhotoEditor,
}) => {
  const { isMobile } = useBreakpoint();

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        justifyContent: 'flex-end',
        zIndex: 999,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: isMobile ? '100%' : '380px',
          height: '100%',
          backgroundColor: '#0f172a',
          borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          color: '#f8fafc',
          boxSizing: 'border-box',
          overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>Profile</h3>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#94a3b8',
              fontSize: '18px',
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <UserAvatar
            principalId={profile.principalId}
            displayName={profile.displayName}
            size="lg"
            onClick={onOpenPhotoEditor}
          />
          <div>
            <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>{profile.displayName || profile.principalId}</h4>
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#94a3b8' }}>{`@${profile.principalId}`}</p>
          </div>
        </div>

        {profile.bio && (
          <p style={{ margin: 0, fontSize: '13px', color: '#cbd5e1', lineHeight: 1.5 }}>{profile.bio}</p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <h5 style={{ margin: 0, fontSize: '12px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' }}>
            Badges & Recognition
          </h5>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {activeBadges.map((b) => (
              <span
                key={b.badgeId}
                style={{
                  padding: '4px 8px',
                  backgroundColor: 'rgba(59, 130, 246, 0.12)',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  borderRadius: '6px',
                  fontSize: '11px',
                  color: '#60a5fa',
                }}
              >
                🏅 {b.badgeType}
              </span>
            ))}
            {activeBadges.length === 0 && (
              <span style={{ fontSize: '12px', color: '#64748b' }}>No active badges recorded</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
